import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from "@mui/material";
import { ReactNode, useState } from "react";
import { GREY } from "@/config/mui/colorPalette";
import { EventType } from "@/hooks/client/useClientPortalPaymentPlansMixpanelEvents";
import useMoxieMediaQuery from "@/hooks/misc/useMoxieMediaQuery";
import SlideBottomDialog from "../../modals/slideBottomDialog";
import { AffirmLogo } from "../affirmLogo";
import PaymentPlanSliderCard, {
  PaymentPlanCardBaseProps,
} from "./paymentPlanSliderCard";

export default function AffirmPromotionCard({
  ...props
}: PaymentPlanCardBaseProps) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const isMobile = useMoxieMediaQuery("ipad-hor", "down");

  const onClose = () => setIsDialogOpen(false);

  return (
    <>
      <PaymentPlanSliderCard
        cardProps={{
          title: <AffirmLogo />,
          description:
            "Split your appointment cost into simple payments with no hidden fees — enjoy services now and pay over time with great interest rates.",
        }}
        buttonProps={{
          copy: "Learn more",
          onClick: () => setIsDialogOpen(true),
        }}
        actionProps={{
          eventType: EventType.AFFIRM_LEARN_MORE_CLICKED,
          ...props,
        }}
      />
      {isMobile ? (
        <AffirmPromotionDetailsDrawer isOpen={isDialogOpen} onClose={onClose} />
      ) : (
        <AffirmPromotionDetailsDialog isOpen={isDialogOpen} onClose={onClose} />
      )}
    </>
  );
}

function AffirmPromotionDetailsDrawer({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) {
  return (
    <SlideBottomDialog
      title="Learn more about Affirm"
      withActions={false}
      open={isOpen}
      onClose={onClose}
      PaperProps={{
        sx: {
          height: "100vh",
          "> .MuiDialogContent-root": {
            overflow: "hidden",
          },
        },
      }}
    >
      <AffirmDetails />
    </SlideBottomDialog>
  );
}

function AffirmPromotionDetailsDialog({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) {
  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      PaperProps={{
        sx: {
          p: 2,
          borderRadius: 4,
        },
      }}
    >
      <DialogTitle>
        <Typography
          variant="h3"
          component="div"
          sx={{
            textAlign: "center",
          }}
        >
          Learn more about Affirm
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ p: 0 }}>
        <AffirmDetails />
      </DialogContent>
      <DialogActions sx={{ p: 0, mt: 3 }}>
        <Button variant="contained" onClick={onClose} fullWidth>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}

const AffirmDetails = () => {
  return (
    <Stack gap={2}>
      <Step stepNumber={1} description="Receive treatment in-office." />
      <Step
        stepNumber={2}
        description="Tell your provider you want to check out with Affirm."
      />
      <Step
        stepNumber={3}
        description="Complete the Affirm checkout application on your device in-office."
        additionalBulletPoints={[
          "Provide basic personal and financial details.",
          "Receive an instant decision in seconds.",
        ]}
      />
      <Step
        stepNumber={4}
        description="Review your financing options."
        additionalBulletPoints={[
          "Affirm will confirm how much they will finance for you.",
          "See available payment plans, interest rates, and terms before committing.",
          "No hidden fees, no surprises.",
        ]}
      />
      <Step
        stepNumber={5}
        description="Choose your plan & check out."
        additionalBulletPoints={[
          "Pay nothing today.",
          "Your first payment will be due based on your selected plan.",
        ]}
      />
      <Step
        stepNumber={6}
        description="Make easy monthly payments."
        additionalBulletPoints={[
          "Payments are automatically deducted, or you can manage them via the Affirm app.",
        ]}
      />
    </Stack>
  );
};

const Step = ({
  stepNumber,
  description,
  additionalBulletPoints,
}: {
  stepNumber: number;
  description: string | ReactNode;
  additionalBulletPoints?: string[];
}) => {
  return (
    <Stack gap={1}>
      <Chip
        label={`Step ${stepNumber}`}
        size="small"
        sx={{ width: "fit-content" }}
      />
      <Typography variant="subtitleDefault">{description}</Typography>
      {additionalBulletPoints?.map((content) => {
        return (
          <Typography key={content} variant="paragraphSmall">
            <span style={{ color: GREY[20] }}>•</span> {content}
          </Typography>
        );
      })}
    </Stack>
  );
};
